import { normalizeTableElement } from "./table-config";
import moment from "moment";

const BASE_ELEMENT_STYLES = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "block",
  fontWeight: "normal",
  fontStyle: "normal",
  fontFamily: "Arial",
  fontSize: "14px",
  minHeight: "14px",
  maxWidth: "800px",
  color: "#000000",
  backgroundColor: "transparent",
  borderStyle: "none",
  borderWidth: "0px",
  borderColor: "transparent",
  borderRadius: "0px",
  padding: "0px",
  margin: "0px",
  textAlign: "left",
  lineHeight: "normal",
  opacity: 1,
  textWrap: false,
};

const PLACEHOLDER_DEFAULT_STYLES = {
  "{COMPANY_LOGO}": {
    width: "100px",
    objectFit: "contain",
    objectPosition: "top",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "{TABLE}": {
    tableLayout: "fixed",
    borderCollapse: "collapse",
    width: "100%",
  },
  "{LABEL}": {
    width: "60px",
    maxWidth: "150px",
    fontSize: "14px",
    minHeight: "14px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "#000000",
  },
  "{LINE}": {
    width: "200px",
    height: "0px",
    borderStyle: "solid",
    borderWidth: "1px",
    borderColor: "#000000",
    backgroundColor: "transparent",
    minHeight: "0px",
    maxWidth: "800px",
    display: "block",
  },
};

const STYLE_KEY_MAP = {
  "white-space": "whiteSpace",
  "text-overflow": "textOverflow",
  "min-width": "minWidth",
  "max-width": "maxWidth",
  "font-family": "fontFamily",
  "font-size": "fontSize",
  "font-weight": "fontWeight",
  "font-style": "fontStyle",
  "text-align": "textAlign",
  "line-height": "lineHeight",
  "text-wrap": "textWrap",
  "object-fit": "objectFit",
  "object-position": "objectPosition",
  "border-color": "borderColor",
  "border-width": "borderWidth",
  "border-style": "borderStyle",
  "border-top-color": "borderTopColor",
  "border-top-width": "borderTopWidth",
  "border-top-style": "borderTopStyle",
  "border-right-color": "borderRightColor",
  "border-right-width": "borderRightWidth",
  "border-right-style": "borderRightStyle",
  "border-bottom-color": "borderBottomColor",
  "border-bottom-width": "borderBottomWidth",
  "border-bottom-style": "borderBottomStyle",
  "border-left-color": "borderLeftColor",
  "border-left-width": "borderLeftWidth",
  "border-left-style": "borderLeftStyle",
  "border-radius": "borderRadius",
  "background-color": "backgroundColor",
};

const MODE_VISIBILITY = {
  canvas: {},
  print: {
    "{Footer_Text}": false,
  },
};

const IMAGE_PLACEHOLDERS = new Set([
  "{COMPANY_LOGO}",
  "{SECONDARY_LOGO}",
  "{BACKGROUND_IMAGE}",
]);

export const isElementVisibleInMode = (element, mode = "canvas") => {
  if (!element) {
    return false;
  }
  const modeRules = MODE_VISIBILITY[mode] || {};
  if (Object.prototype.hasOwnProperty.call(modeRules, element.fieldPlaceHolder)) {
    return modeRules[element.fieldPlaceHolder];
  }
  return true;
};

const toCamelCaseStyle = (styles = {}) => {
  const styleObj = { ...(styles || {}) };
  Object.keys(STYLE_KEY_MAP).forEach((sourceKey) => {
    const targetKey = STYLE_KEY_MAP[sourceKey];
    if (
      typeof styleObj[targetKey] === "undefined" &&
      typeof styleObj[sourceKey] !== "undefined"
    ) {
      styleObj[targetKey] = styleObj[sourceKey];
    }
  });
  return styleObj;
};

export const getDefaultElementStyles = (fieldPlaceHolder) => ({
  ...BASE_ELEMENT_STYLES,
  ...(PLACEHOLDER_DEFAULT_STYLES[fieldPlaceHolder] || {}),
});

export const normalizeElementStyles = (element = {}) => {
  const defaultStyles = getDefaultElementStyles(element.fieldPlaceHolder);
  const incomingStyles = toCamelCaseStyle(element.styles || {});

  const mergedStyles = {
    ...defaultStyles,
    ...incomingStyles,
  };

  // Legacy forced min widths introduce canvas/print drift.
  delete mergedStyles["min-width"];
  delete mergedStyles.minWidth;

  if (typeof mergedStyles.textWrap === "string") {
    mergedStyles.textWrap = mergedStyles.textWrap === "true";
  }

  return mergedStyles;
};

export const sanitizeParagraphHtml = (html) => {
  if (!html) {
    return "";
  }
  return String(html).replace(/^<p>|<\/p>$/g, "").replace(/<\/?span[^>]*>/g, "");
};

export const isImagePlaceholder = (placeholder) =>
  IMAGE_PLACEHOLDERS.has(String(placeholder || "").trim().toUpperCase());

export const getElementRenderConfig = (element = {}, mode = "canvas") => {
  const normalizedElement =
    element.fieldPlaceHolder === "{TABLE}" ? normalizeTableElement(element) : element;
  const placeholder = normalizedElement.fieldPlaceHolder;
  const rawImageSource =
    normalizedElement.fieldName ||
    normalizedElement.link?.data?.link ||
    normalizedElement.link ||
    normalizedElement.image ||
    normalizedElement.url ||
    "";
  const imageSource =
    isImagePlaceholder(placeholder) && rawImageSource
      ? String(rawImageSource).trim()
      : "";
  const content =
    placeholder === "{TODAY_DATE}"
      ? moment().format("DD.MM.YYYY")
      : normalizedElement.fieldName || placeholder || "";

  return {
    mode,
    visible: isElementVisibleInMode(normalizedElement, mode),
    element: normalizedElement,
    styles: normalizeElementStyles(normalizedElement),
    placeholder,
    content,
    imageSource,
  };
};
